import { FeatureFlag } from '@ampcore/ampcom-contracts'
import { InvitationService } from '@ampcore/client-side-services'
import { useFeatureFlag } from '@ampcore/feature-flags-react'
import React from 'react'

import {
  APWG_SCHEDULER_KEY,
  RedirectorInvitationConfig
} from './Redirector.type'

const INVITATION_SCHEDULE_URL =
  'https://www.ameriprise.com/collaboration/schedule'

/**
 * Destination used while the API-generated scheduler link is disabled. When the
 * flag is off, this static link is the invitation target and the invitation
 * lookup is never called.
 */
const STATIC_SCHEDULER_URL =
  'https://ameriprise-aac.my.salesforce-sites.com/cts?Id=8upUKVR3mX.tsIf8CAU5ttMEu06wO2tieVEwuwmz5lc-Z'

const buildScheduleUrl = (invitationId: string): string => {
  const url = new URL(INVITATION_SCHEDULE_URL)
  url.searchParams.set('Id', invitationId)
  return url.toString()
}

/**
 * Maps an invitation key to its lookup. Only the APWG scheduler is supported;
 * any other key resolves to null so the caller redirects to the error page.
 */
const resolveInvitationId = (invitationKey: string): Promise<string | null> => {
  if (invitationKey === APWG_SCHEDULER_KEY) {
    return new InvitationService().getApwgInvitationId()
  }
  return Promise.resolve(null)
}

export type InvitationRedirectorProps = Omit<
  RedirectorInvitationConfig,
  'variant'
>

export const InvitationRedirector: React.FC<InvitationRedirectorProps> = ({
  invitationKey
}) => {
  const { flagValue: useApiGeneratedLink, isReady } = useFeatureFlag(
    FeatureFlag.RELEASE_APWG_API_GENERATED_SCHEDULER_LINK,
    false
  )

  React.useEffect(() => {
    // Hold the redirect until the flag read settles. If the read times out,
    // the flag resolves to its default (static link) — the intended behavior
    // when the flag service is unreachable.
    if (!isReady) return

    if (!useApiGeneratedLink) {
      globalThis.location.replace(STATIC_SCHEDULER_URL)
      return
    }

    resolveInvitationId(invitationKey)
      .then((invitationId: string | null) => {
        if (invitationId) {
          globalThis.location.replace(buildScheduleUrl(invitationId))
        } else {
          throw new Error('No invitation ID returned')
        }
      })
      .catch(() => {
        console.error('Failed to resolve invitation')
        globalThis.location.replace('/error')
      })
  }, [invitationKey, isReady, useApiGeneratedLink])
  return null
}
